import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowLeft, CheckCircle2, Plus, Search } from "lucide-react";
import { api } from "@jourfx/convex/client";
import {
  Badge,
  BirthdayPicker,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
} from "@jourfx/ui";

import { PageHeader } from "../../components/PageHeader";
import { useDateFormat } from "../../hooks/useDateFormat";
import { parseLocalDateTimeInput } from "./shared";

type OrganizationOption = {
  _id: string;
  name: string;
};

type TemplateOption = {
  _id: string;
  name: string;
  duration: number;
  color?: string;
  description?: string;
};

type GutachterOption = {
  _id: string;
  name: string;
  email?: string;
};

type VersicherteOption = {
  _id: string;
  organizationId: string;
  organizationName?: string | null;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  accessCode?: string;
};

type VersicherteDetail = VersicherteOption & {
  anrede?: string;
  dateOfBirth?: string;
  versichertennummer?: string;
  notes?: string;
};

type CreateResult = {
  appointmentId: string;
  accessCode: string;
};

type DurationPolicy = {
  allowedDurations: number[];
  appointmentDuration: number;
};

const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

function getInitialSearchParams() {
  if (typeof window === "undefined") {
    return { organizationId: "", versicherteId: "" };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    organizationId: params.get("organizationId") ?? "",
    versicherteId: params.get("versicherteId") ?? "",
  };
}

export function AppointmentCreate() {
  const apiRef = api as any;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation("appointments");
  const { formatDateTime } = useDateFormat();
  const initialSearch = useMemo(() => getInitialSearchParams(), []);

  const [step, setStep] = useState(1);
  const [organizationId, setOrganizationId] = useState(initialSearch.organizationId || "");
  const [templateId, setTemplateId] = useState("");
  const [versicherteSearch, setVersicherteSearch] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState(
    initialSearch.versicherteId
  );
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const [gutachterId, setGutachterId] = useState("");
  const [scheduledAtInput, setScheduledAtInput] = useState("");
  const [duration, setDuration] = useState<number>(30);
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [notes, setNotes] = useState("");
  const [sendEmailInvite, setSendEmailInvite] = useState(true);
  const [enableReminders, setEnableReminders] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createResult, setCreateResult] = useState<CreateResult | null>(null);
  const [templateDurationFallback, setTemplateDurationFallback] = useState<{
    templateName: string;
    legacyDuration: number;
    fallbackDuration: number;
  } | null>(null);
  const [profileDraft, setProfileDraft] = useState({
    anrede: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    versichertennummer: "",
    email: "",
    phone: "",
    notes: "",
  });

  const organizations = useQuery(
    apiRef.admin.organizations.list,
    { status: "active", limit: 100 }
  ) as { organizations: OrganizationOption[] } | undefined;

  const selectedOrgId = organizationId || undefined;

  const templates = useQuery(
    apiRef.admin.appointmentTemplates.list,
    selectedOrgId ? { organizationId: selectedOrgId as any } : "skip"
  ) as TemplateOption[] | undefined;
  const durationPolicy = useQuery(
    apiRef.admin.appointmentTemplates.getDurationPolicyForOrganization,
    selectedOrgId ? { organizationId: selectedOrgId as any } : "skip"
  ) as DurationPolicy | undefined;

  const versicherte = useQuery(
    apiRef.admin.versicherte.list,
    selectedOrgId
      ? {
          organizationId: selectedOrgId as any,
          search: versicherteSearch.trim() || undefined,
          limit: 100,
        }
      : "skip"
  ) as VersicherteOption[] | undefined;

  const selectedProfile = useQuery(
    apiRef.admin.versicherte.get,
    selectedProfileId
      ? { versichertenProfileId: selectedProfileId as any }
      : "skip"
  ) as VersicherteDetail | null | undefined;

  const gutachter = useQuery(
    apiRef.admin.appointments.listGutachter,
    selectedOrgId ? { organizationId: selectedOrgId as any } : "skip"
  ) as GutachterOption[] | undefined;

  const createProfile = useMutation(apiRef.admin.versicherte.create);
  const createAppointment = useMutation(apiRef.admin.appointments.create);

  const selectedTemplate = useMemo(
    () => templates?.find((entry) => entry._id === templateId) ?? null,
    [templateId, templates]
  );
  const allowedDurations = useMemo(
    () =>
      durationPolicy?.allowedDurations?.length
        ? durationPolicy.allowedDurations
        : [30, 60, 90],
    [durationPolicy]
  );
  const defaultDuration = durationPolicy?.appointmentDuration ?? allowedDurations[0] ?? 30;
  const allowedDurationsKey = useMemo(
    () => allowedDurations.join(","),
    [allowedDurations]
  );
  const selectedTemplateKey = selectedTemplate
    ? `${selectedTemplate._id}:${selectedTemplate.duration}`
    : "";

  const selectedExaminer = useMemo(
    () => gutachter?.find((entry) => entry._id === gutachterId) ?? null,
    [gutachter, gutachterId]
  );

  const scheduledAt = useMemo(
    () => parseLocalDateTimeInput(scheduledAtInput),
    [scheduledAtInput]
  );

  useEffect(() => {
    if (!selectedTemplate) {
      setTemplateDurationFallback((current) => (current === null ? current : null));
      return;
    }

    if (allowedDurations.includes(selectedTemplate.duration)) {
      setTemplateDurationFallback((current) => (current === null ? current : null));
      setDuration((current) =>
        current === selectedTemplate.duration ? current : selectedTemplate.duration
      );
      return;
    }

    setTemplateDurationFallback((current) =>
      current?.templateName === selectedTemplate.name &&
      current.legacyDuration === selectedTemplate.duration &&
      current.fallbackDuration === defaultDuration
        ? current
        : {
            templateName: selectedTemplate.name,
            legacyDuration: selectedTemplate.duration,
            fallbackDuration: defaultDuration,
          }
    );
    setDuration((current) => (current === defaultDuration ? current : defaultDuration));
  }, [allowedDurationsKey, defaultDuration, selectedTemplateKey]);

  useEffect(() => {
    if (!selectedProfile || organizationId) return;
    setOrganizationId(selectedProfile.organizationId);
  }, [organizationId, selectedProfile]);

  const canContinueFromOrg = Boolean(selectedOrgId);
  const canContinueFromTemplate = Boolean(selectedTemplate);
  const canContinueFromProfile = Boolean(selectedProfileId);
  const canContinueFromSchedule = Boolean(
    gutachterId && scheduledAt && allowedDurations.includes(duration)
  );
  const canSubmit =
    Boolean(selectedOrgId) &&
    Boolean(selectedTemplate) &&
    Boolean(selectedProfileId) &&
    Boolean(gutachterId) &&
    Boolean(scheduledAt) &&
    allowedDurations.includes(duration);

  const handleInlineCreate = async () => {
    if (!selectedOrgId) {
      toast({
        title: t("create.toasts.selectOrganizationTitle"),
        description: t("create.toasts.selectOrganizationDescription"),
        variant: "destructive",
      });
      return;
    }

    if (!profileDraft.firstName.trim() || !profileDraft.lastName.trim()) {
      toast({
        title: t("create.toasts.profileMissingTitle"),
        description: t("create.toasts.profileMissingDescription"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createProfile({
        organizationId: selectedOrgId as any,
        anrede: profileDraft.anrede || undefined,
        firstName: profileDraft.firstName.trim(),
        lastName: profileDraft.lastName.trim(),
        dateOfBirth: profileDraft.dateOfBirth || undefined,
        versichertennummer: profileDraft.versichertennummer || undefined,
        email: profileDraft.email || undefined,
        phone: profileDraft.phone || undefined,
        notes: profileDraft.notes || undefined,
      });

      setSelectedProfileId(result.versichertenProfileId);
      setShowInlineCreate(false);
      setProfileDraft({
        anrede: "",
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        versichertennummer: "",
        email: "",
        phone: "",
        notes: "",
      });
      toast({
        title: t("create.toasts.profileCreatedTitle"),
        description: t("create.toasts.profileCreatedDescription", {
          accessCode: result.accessCode,
        }),
      });
    } catch (error) {
      toast({
        title: t("create.toasts.errorTitle"),
        description: error instanceof Error ? error.message : t("create.toasts.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAppointment = async () => {
    if (!canSubmit || !selectedOrgId || !scheduledAt) return;

    setIsSubmitting(true);
    try {
      const result = await createAppointment({
        organizationId: selectedOrgId as any,
        templateId: templateId as any,
        versichertenProfileId: selectedProfileId as any,
        gutachterId: gutachterId as any,
        scheduledAt,
        duration,
        timezone,
        notes: notes.trim() || undefined,
        sendEmailInvite,
        enableReminders,
      });

      setCreateResult(result);
      setStep(5);
    } catch (error) {
      toast({
        title: t("create.toasts.errorTitle"),
        description: error instanceof Error ? error.message : t("create.toasts.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link
        to="/appointments"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("detail.back")}
      </Link>

      <PageHeader title={t("create.title")} description={t("create.subtitle")} />

      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: 1, label: t("create.steps.organization") },
          { id: 2, label: t("create.steps.template") },
          { id: 3, label: t("create.steps.patient") },
          { id: 4, label: t("create.steps.schedule") },
          { id: 5, label: t("create.steps.review") },
        ]
          .filter(Boolean)
          .map((entry) => {
            const current = entry as { id: number; label: string };
            const active = step === current.id;
            const complete = step > current.id;
            return (
              <Badge
                key={current.id}
                variant={active ? "default" : complete ? "secondary" : "outline"}
                className="px-3 py-1"
              >
                {current.label}
              </Badge>
            );
          })}
      </div>

      {step === 1 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("create.steps.organization")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="appointment-org">{t("create.organization")}</Label>
              <select
                id="appointment-org"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={organizationId}
                onChange={(event) => setOrganizationId(event.target.value)}
              >
                <option value="">{t("create.selectOrganization")}</option>
                {organizations?.organizations?.map((organization) => (
                  <option key={organization._id} value={organization._id}>
                    {organization.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end">
              <Button disabled={!canContinueFromOrg} onClick={() => setStep(2)}>
                {t("create.next")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 2 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("create.steps.template")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              {templates?.map((template) => (
                <button
                  key={template._id}
                  type="button"
                  onClick={() => setTemplateId(template._id)}
                  className={`rounded-lg border p-4 text-left transition-colors ${
                    templateId === template._id
                      ? "border-primary bg-primary/5"
                      : "hover:bg-accent"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: template.color || "#0f766e" }}
                    />
                    <span className="font-medium">{template.name}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t("list.minutes", { count: template.duration })}
                  </p>
                  {template.description ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {template.description}
                    </p>
                  ) : null}
                </button>
              ))}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                {t("create.back")}
              </Button>
              <Button disabled={!canContinueFromTemplate} onClick={() => setStep(3)}>
                {t("create.next")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 3 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("create.steps.patient")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                value={versicherteSearch}
                onChange={(event) => setVersicherteSearch(event.target.value)}
                placeholder={t("create.searchPatient")}
              />
            </div>

            <div className="space-y-2 rounded-lg border p-3">
              {versicherte?.map((profile) => {
                const selected = selectedProfileId === profile._id;
                return (
                  <button
                    key={profile._id}
                    type="button"
                    onClick={() => setSelectedProfileId(profile._id)}
                    className={`flex w-full items-start justify-between rounded-md border px-3 py-2 text-left ${
                      selected ? "border-primary bg-primary/5" : "border-transparent hover:bg-accent"
                    }`}
                  >
                    <div>
                      <div className="font-medium">
                        {profile.firstName} {profile.lastName}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {profile.email || profile.phone || t("create.noContact")}
                      </div>
                    </div>
                    {profile.organizationName ? (
                      <span className="text-xs text-muted-foreground">
                        {profile.organizationName}
                      </span>
                    ) : null}
                  </button>
                );
              })}

              {versicherte?.length === 0 ? (
                <p className="py-4 text-sm text-muted-foreground">{t("create.noProfiles")}</p>
              ) : null}
            </div>

            <div className="rounded-lg border border-dashed p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-medium">{t("create.inlineCreateTitle")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("create.inlineCreateDescription")}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInlineCreate((current) => !current)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {showInlineCreate ? t("create.hideInlineCreate") : t("create.showInlineCreate")}
                </Button>
              </div>

              {showInlineCreate ? (
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="profile-anrede">{t("create.profile.anrede")}</Label>
                    <Input
                      id="profile-anrede"
                      value={profileDraft.anrede}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          anrede: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-dob">{t("create.profile.dateOfBirth")}</Label>
                    <BirthdayPicker
                      id="profile-dob"
                      value={profileDraft.dateOfBirth}
                      onChange={(date) =>
                        setProfileDraft((current) => ({
                          ...current,
                          dateOfBirth: date,
                        }))
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-first-name">{t("create.profile.firstName")}</Label>
                    <Input
                      id="profile-first-name"
                      value={profileDraft.firstName}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          firstName: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-last-name">{t("create.profile.lastName")}</Label>
                    <Input
                      id="profile-last-name"
                      value={profileDraft.lastName}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          lastName: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-email">{t("create.profile.email")}</Label>
                    <Input
                      id="profile-email"
                      value={profileDraft.email}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          email: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-phone">{t("create.profile.phone")}</Label>
                    <Input
                      id="profile-phone"
                      value={profileDraft.phone}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          phone: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-number">
                      {t("create.profile.versichertennummer")}
                    </Label>
                    <Input
                      id="profile-number"
                      value={profileDraft.versichertennummer}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          versichertennummer: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="profile-notes">{t("create.profile.notes")}</Label>
                    <textarea
                      id="profile-notes"
                      className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={profileDraft.notes}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          notes: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <Button type="button" disabled={isSubmitting} onClick={handleInlineCreate}>
                      {t("create.profile.create")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>

            {selectedProfile ? (
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="font-medium">
                  {selectedProfile.firstName} {selectedProfile.lastName}
                </div>
                <div className="text-sm text-muted-foreground">
                  {selectedProfile.email || selectedProfile.phone || t("create.noContact")}
                </div>
              </div>
            ) : null}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                {t("create.back")}
              </Button>
              <Button disabled={!canContinueFromProfile} onClick={() => setStep(4)}>
                {t("create.next")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 4 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("create.steps.schedule")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="appointment-gutachter">{t("create.examiner")}</Label>
                <select
                  id="appointment-gutachter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={gutachterId}
                  onChange={(event) => setGutachterId(event.target.value)}
                >
                  <option value="">{t("create.selectExaminer")}</option>
                  {gutachter?.map((entry) => (
                    <option key={entry._id} value={entry._id}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="appointment-scheduled-at">{t("create.scheduledAt")}</Label>
                <Input
                  id="appointment-scheduled-at"
                  type="datetime-local"
                  value={scheduledAtInput}
                  onChange={(event) => setScheduledAtInput(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="appointment-duration">{t("create.duration")}</Label>
                <select
                  id="appointment-duration"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={String(duration)}
                  onChange={(event) => setDuration(Number(event.target.value || 0))}
                  data-testid="appointment-duration-select"
                >
                  <option value="">{t("create.selectDuration")}</option>
                  {allowedDurations.map((entry) => (
                    <option key={entry} value={entry}>
                      {t("list.minutes", { count: entry })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="appointment-timezone">{t("create.timezone")}</Label>
                <Input
                  id="appointment-timezone"
                  value={timezone}
                  onChange={(event) => setTimezone(event.target.value)}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="appointment-notes">{t("create.notes")}</Label>
                <textarea
                  id="appointment-notes"
                  className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>
            </div>

            {templateDurationFallback ? (
              <div
                className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm text-warning"
                data-testid="create-duration-fallback"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {t("create.durationFallbackNotice", {
                    templateName: templateDurationFallback.templateName,
                    legacyDuration: templateDurationFallback.legacyDuration,
                    fallbackDuration: templateDurationFallback.fallbackDuration,
                  })}
                </span>
              </div>
            ) : null}

            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
                <input
                  type="checkbox"
                  checked={sendEmailInvite}
                  onChange={(event) => setSendEmailInvite(event.target.checked)}
                />
                <span>{t("create.sendEmailInvite")}</span>
              </label>
              <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
                <input
                  type="checkbox"
                  checked={enableReminders}
                  onChange={(event) => setEnableReminders(event.target.checked)}
                />
                <span>{t("create.enableReminders")}</span>
              </label>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}>
                {t("create.back")}
              </Button>
              <Button disabled={!canContinueFromSchedule} onClick={() => setStep(5)}>
                {t("create.next")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 5 ? (
        <Card>
          <CardHeader>
            <CardTitle>{createResult ? t("create.successTitle") : t("create.steps.review")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {createResult ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-900">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="h-5 w-5" />
                    {t("create.successDescription")}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-1">
                  <div className="rounded-lg border p-4">
                    <div className="text-sm text-muted-foreground">{t("credentials.accessCode")}</div>
                    <div className="mt-1 font-mono text-lg font-semibold">
                      {createResult.accessCode}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/appointments/$appointmentId"
                    params={{ appointmentId: createResult.appointmentId as any }}
                  >
                    <Button>{t("create.openAppointment")}</Button>
                  </Link>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCreateResult(null);
                      navigate({ to: "/appointments" });
                    }}
                  >
                    {t("create.backToList")}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border p-4">
                    <div className="text-sm text-muted-foreground">{t("create.summary.organization")}</div>
                    <div className="font-medium">
                      {organizations?.organizations?.find((entry) => entry._id === selectedOrgId)?.name ||
                        "-"}
                    </div>
                  </div>
                  <div className="rounded-lg border p-4">
                    <div className="text-sm text-muted-foreground">{t("create.summary.template")}</div>
                    <div className="font-medium">{selectedTemplate?.name || "-"}</div>
                  </div>
                  <div className="rounded-lg border p-4">
                    <div className="text-sm text-muted-foreground">{t("create.summary.patient")}</div>
                    <div className="font-medium">
                      {selectedProfile
                        ? `${selectedProfile.firstName} ${selectedProfile.lastName}`
                        : "-"}
                    </div>
                  </div>
                  <div className="rounded-lg border p-4">
                    <div className="text-sm text-muted-foreground">{t("create.summary.examiner")}</div>
                    <div className="font-medium">{selectedExaminer?.name || "-"}</div>
                  </div>
                  <div className="rounded-lg border p-4">
                    <div className="text-sm text-muted-foreground">{t("create.summary.dateTime")}</div>
                    <div className="font-medium">
                      {scheduledAt ? formatDateTime(scheduledAt) : "-"}
                    </div>
                  </div>
                  <div className="rounded-lg border p-4">
                    <div className="text-sm text-muted-foreground">{t("create.summary.duration")}</div>
                    <div className="font-medium">{t("list.minutes", { count: duration })}</div>
                  </div>
                </div>

                {notes ? (
                  <div className="rounded-lg border p-4">
                    <div className="text-sm text-muted-foreground">{t("create.summary.notes")}</div>
                    <div className="mt-1 whitespace-pre-wrap text-sm">{notes}</div>
                  </div>
                ) : null}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(4)}>
                    {t("create.back")}
                  </Button>
                  <Button disabled={!canSubmit || isSubmitting} onClick={handleCreateAppointment}>
                    {t("create.submit")}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
